import {
  isCanonicalPurchaseUnit,
} from "../constants/purchaseUnits";

export const PURCHASE_ITEMS_BATCH_LIMIT = 200;
export const BASE_UNIT_OPTIONS = [
  { value: "g", label: "Gramos (g)" },
  { value: "ml", label: "Mililitros (ml)" },
  { value: "unit", label: "Unidad" },
  { value: "package", label: "Paquete" },
];

let rowSequence = 0;

export function normalizeDecimalInput(value) {
  return String(value ?? "").trim().replace(",", ".");
}

export function formatDecimalForInput(value) {
  const normalized = normalizeDecimalInput(value);
  if (!/^\d+\.\d+$/.test(normalized)) return normalized;
  return normalized.replace(/\.?0+$/, "");
}

export function makeEmptyWorkspaceRow(ingredient = null) {
  rowSequence += 1;
  return {
    clientRowId: `new-${Date.now()}-${rowSequence}`,
    id: "",
    ingredient,
    supplier: null,
    brand: "",
    presentation: "",
    purchaseUnit: "",
    quantityPerUnit: "",
    cost: "",
    stockCountUnit: "package",
    originalPurchaseUnit: "",
    rowState: "empty",
    errors: {},
  };
}

function parseCatalogQuantity(value) {
  const normalized = String(value || "").trim();
  if (!normalized) return null;
  if (/^\d{1,3}(?:\.\d{3})+$/.test(normalized)) {
    return Number(normalized.replace(/\./g, ""));
  }
  const decimal = normalized.includes(",")
    ? normalized.replace(/\./g, "").replace(",", ".")
    : normalized;
  const parsed = Number(decimal);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

function formatSuggestedQuantity(value) {
  return Number.isInteger(value) ? String(value) : String(value).replace(/\.?0+$/, "");
}

export function getBasePresentationSuggestion(ingredient) {
  const name = String(ingredient?.name || "").trim().replace(/\s+/g, " ");
  const baseUnit = String(ingredient?.baseUnit || "").toLowerCase();

  if (!name) return null;
  if (baseUnit === "unit") {
    return {
      brand: name,
      presentation: "Unidad",
      purchaseUnit: "Unidad",
      quantityPerUnit: "1",
      stockCountUnit: "unit",
    };
  }

  const match = name.match(/(?:^|\s)(\d[\d.,]*)\s*(ML|G|L|KG)\s*$/i);
  if (!match) {
    return {
      brand: name,
      presentation: "",
      purchaseUnit: "",
      quantityPerUnit: "",
      stockCountUnit: "package",
    };
  }

  const parsedQuantity = parseCatalogQuantity(match[1]);
  const sourceUnit = match[2].toUpperCase();
  const conversion = sourceUnit === "L"
    ? { baseUnit: "ml", factor: 1000 }
    : sourceUnit === "KG"
      ? { baseUnit: "g", factor: 1000 }
      : { baseUnit: sourceUnit.toLowerCase(), factor: 1 };
  if (!parsedQuantity || conversion.baseUnit !== baseUnit) {
    return {
      brand: name,
      presentation: "",
      purchaseUnit: "",
      quantityPerUnit: "",
      stockCountUnit: "package",
    };
  }

  const brand = name
    .slice(0, match.index)
    .trim()
    .replace(/[\s·–—-]+$/, "") || name;
  return {
    brand,
    presentation: `${match[1]} ${sourceUnit}`,
    purchaseUnit: "Unidad",
    quantityPerUnit: formatSuggestedQuantity(parsedQuantity * conversion.factor),
    stockCountUnit: "unit",
  };
}

export function makeBasePresentationWorkspaceRow(ingredient) {
  const row = makeEmptyWorkspaceRow(ingredient);
  const suggestion = getBasePresentationSuggestion(ingredient);
  return suggestion ? { ...row, ...suggestion, rowState: "new" } : row;
}

export function purchaseItemToWorkspaceRow(item) {
  return {
    clientRowId: `item-${item.id}`,
    id: Number(item.id),
    ingredient: item.ingredient || null,
    supplier: item.supplier || null,
    brand: String(item.brand || ""),
    presentation: String(item.presentation || ""),
    purchaseUnit: String(item.purchaseUnit || ""),
    quantityPerUnit: formatDecimalForInput(item.quantityPerUnit),
    cost: formatDecimalForInput(item.cost),
    stockCountUnit: item.stockCountUnit || "package",
    originalPurchaseUnit: String(item.purchaseUnit || ""),
    rowState: "unchanged",
    errors: {},
  };
}

export function isWorkspaceRowBlank(row) {
  return (
    !row.id &&
    !row.ingredient &&
    !row.supplier &&
    !String(row.brand || "").trim() &&
    !String(row.presentation || "").trim() &&
    !String(row.purchaseUnit || "").trim() &&
    !normalizeDecimalInput(row.quantityPerUnit) &&
    !normalizeDecimalInput(row.cost)
  );
}

export function isWorkspaceRowDirty(row) {
  if (row.id) return row.rowState === "edited" || row.rowState === "error";
  return !isWorkspaceRowBlank(row);
}

function validateDecimal(value, { scale, integerDigits, positive, label }) {
  const normalized = normalizeDecimalInput(value);
  if (!normalized) return `${label} es obligatorio.`;
  const match = /^(\d+)(?:\.(\d+))?$/.exec(normalized);
  if (!match) return `${label} debe ser un número válido.`;
  const integer = match[1].replace(/^0+(?=\d)/, "");
  const fraction = match[2] || "";
  if (integer.length > integerDigits || fraction.length > scale) {
    return `${label} admite hasta ${integerDigits} enteros y ${scale} decimales.`;
  }
  const zero = Number(normalized) === 0;
  if (positive && zero) return `${label} debe ser mayor a cero.`;
  return "";
}

export function validateWorkspaceRow(row) {
  const errors = {};
  const brand = String(row.brand || "").trim();
  const presentation = String(row.presentation || "").trim();
  const purchaseUnit = String(row.purchaseUnit || "").trim();
  const preservesHistorical = Boolean(
    row.id &&
      !isCanonicalPurchaseUnit(row.originalPurchaseUnit) &&
      purchaseUnit === row.originalPurchaseUnit
  );

  if (!row.ingredient?.id) errors.ingredient = "Seleccioná un insumo activo.";
  if (!row.supplier?.id) errors.supplier = "Seleccioná un proveedor activo.";
  if (!brand) errors.brand = "La marca es obligatoria.";
  else if (brand.length > 150) errors.brand = "Máximo 150 caracteres.";
  if (!presentation) errors.presentation = "La presentación es obligatoria.";
  else if (presentation.length > 200) errors.presentation = "Máximo 200 caracteres.";
  if (!purchaseUnit) errors.purchaseUnit = "La unidad de compra es obligatoria.";
  else if (!isCanonicalPurchaseUnit(purchaseUnit) && !preservesHistorical) {
    errors.purchaseUnit = "Seleccioná una unidad de compra de la lista.";
  }
  const quantityError = validateDecimal(row.quantityPerUnit, {
    scale: 4,
    integerDigits: 8,
    positive: true,
    label: "La cantidad",
  });
  if (quantityError) errors.quantityPerUnit = quantityError;
  const costError = validateDecimal(row.cost, {
    scale: 2,
    integerDigits: 10,
    positive: false,
    label: "El costo",
  });
  if (costError) errors.cost = costError;
  if (!BASE_UNIT_OPTIONS.some(({ value }) => value === row.stockCountUnit)) {
    errors.stockCountUnit = "Seleccioná una unidad de conteo.";
  }
  return errors;
}

export function workspaceRowToPayload(row) {
  const purchaseUnit = String(row.purchaseUnit || "").trim();
  const preservesHistorical = Boolean(
    row.id &&
      !isCanonicalPurchaseUnit(row.originalPurchaseUnit) &&
      purchaseUnit === row.originalPurchaseUnit
  );
  return {
    clientRowId: row.clientRowId,
    ...(row.id ? { id: Number(row.id) } : {}),
    ingredientId: Number(row.ingredient.id),
    supplierId: Number(row.supplier.id),
    brand: String(row.brand).trim(),
    presentation: String(row.presentation).trim(),
    ...(!preservesHistorical ? { purchaseUnit } : {}),
    quantityPerUnit: normalizeDecimalInput(row.quantityPerUnit),
    cost: normalizeDecimalInput(row.cost),
    stockCountUnit: row.stockCountUnit,
  };
}
